import { getSupabaseClient } from '../lib/supabase';
import {
  HomePageData,
  SeekerProfile,
  Match,
  MatchStatus,
  Session,
  Resource,
  Guide,
  PackageOffer,
  PackageOfferStatus,
  PackageOfferUnit,
  PackageOfferUnitStatus
} from '../types';
import { OnboardingAnswers } from '../types/onboarding';

type ScheduledSessionRow = {
  id: string;
  session_date: string | null;
  session_time: string | null;
  duration_minutes: number | null;
  session_status: string | null;
};

type PackageOfferUnitRow = {
  id: number;
  template_component_id: number | null;
  component_name: string | null;
  component_type: string | null;
  unit_index: number | null;
  status: string | null;
  unit_price_cents: number | null;
  notes: string | null;
  scheduled_session_id: string | null;
  scheduled_session?: ScheduledSessionRow | null;
};

type PackageOfferRow = {
  id: string;
  name: string | null;
  description: string | null;
  duration_months: number | null;
  status: string | null;
  subtotal_cents: number | null;
  total_cents: number | null;
  created_at: string | null;
  package_offer_units?: PackageOfferUnitRow[] | null;
};

const VISIBLE_PACKAGE_STATUSES: PackageOfferStatus[] = ['draft', 'sent', 'accepted'];

const DEFAULT_COMPONENT_TYPE = 'session';

export class SeekerDataService {
  private _supabase: ReturnType<typeof getSupabaseClient> | null = null;

  private get supabase() {
    if (!this._supabase) {
      this._supabase = getSupabaseClient();
    }
    return this._supabase;
  }

  private getStoredSeekerIds(additionalIds: string[] = []): string[] {
    const ids = new Set<string>(additionalIds);
    const storageKeys = [
      'ghost_seeker_id',
      'seeker_id',
      'seekerId',
      'current_seeker_id'
    ];

    storageKeys.forEach(key => {
      const value = typeof window !== 'undefined' ? localStorage.getItem(key) : null;
      if (value && value.trim()) {
        ids.add(value.trim());
      }
    });

    return Array.from(ids);
  }

  async getCurrentUser(): Promise<{ user: any; profile: SeekerProfile | null }> {
    const { data: { user }, error: authError } = await this.supabase.auth.getUser();
    
    if (authError || !user) {
      console.warn('Seeker not authenticated – redirecting to login');
      window.location.href = '/login';
      throw new Error('Not authenticated');
    }

    // Get seeker profile from profiles table
    const { data: profileData, error: profileError } = await this.supabase
      .from('profiles')
      .select('id, full_name, email, phone, created_at')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.warn('Failed to fetch profile:', profileError);
    }

    let seekerImageUrl: string | null = null;
    const { data: seekerRow, error: seekerError } = await this.supabase
      .from('seekers')
      .select('image_url')
      .eq('user_id', user.id)
      .maybeSingle();

    if (seekerError && seekerError.code !== 'PGRST116') {
      console.warn('Failed to fetch seeker image:', seekerError);
    } else if (seekerRow?.image_url) {
      seekerImageUrl = seekerRow.image_url;
    }

    const profile: SeekerProfile | null = profileData ? {
      id: profileData.id,
      name: profileData.full_name,
      email: profileData.email,
      phone: profileData.phone,
      created_at: profileData.created_at,
      imageUrl: seekerImageUrl
    } : null;

    return { user, profile };
  }

  async getOnboardingEditData(): Promise<{
    answers: OnboardingAnswers;
    seekerId: string;
    sessionId: string;
    imageUrl: string | null;
  } | null> {
    const { data: { user }, error: authError } = await this.supabase.auth.getUser();

    if (authError || !user) {
      throw new Error('Not authenticated');
    }

    const seekerId = await this.getSeekerIdForCurrentUser();
    if (!seekerId) {
      return null;
    }

    const { data: basicInfo, error: basicInfoError } = await this.supabase
      .from('seeker_basic_info')
      .select('*')
      .eq('seeker_id', seekerId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (basicInfoError) {
      if (basicInfoError.code === 'PGRST116') {
        return null;
      }
      throw new Error(`Failed to load basic information: ${basicInfoError.message}`);
    }

    if (!basicInfo) {
      return null;
    }

    const sessionId: string = basicInfo.session_id;

    const [{ data: experience, error: experienceError }, { data: medical, error: medicalError }] = await Promise.all([
      this.supabase
        .from('seeker_experience_preferences')
        .select('*')
        .eq('seeker_id', seekerId)
        .eq('session_id', sessionId)
        .maybeSingle(),
      this.supabase
        .from('seeker_medical_and_final')
        .select('*')
        .eq('seeker_id', seekerId)
        .eq('session_id', sessionId)
        .maybeSingle()
    ]);

    if (experienceError && experienceError.code !== 'PGRST116') {
      throw new Error(`Failed to load experience preferences: ${experienceError.message}`);
    }

    if (medicalError && medicalError.code !== 'PGRST116') {
      throw new Error(`Failed to load medical information: ${medicalError.message}`);
    }

    const { profile } = await this.getCurrentUser();

    const { data: seekerRow, error: seekerError } = await this.supabase
      .from('seekers')
      .select('image_url')
      .eq('user_id', user.id)
      .maybeSingle();

    if (seekerError && seekerError.code !== 'PGRST116') {
      console.warn('Failed to load seeker profile photo:', seekerError);
    }

    const answers: OnboardingAnswers = {};

    answers['seeker_email'] = basicInfo.seeker_email;
    answers['seeker_over_18'] = basicInfo.seeker_over_18;
    answers['location_postcode'] = basicInfo.location_postcode;
    // Use seeker_genders array if available (multi-select), otherwise fallback to single value
    if (basicInfo.seeker_genders && Array.isArray(basicInfo.seeker_genders) && basicInfo.seeker_genders.length > 0) {
      answers['seeker_gender'] = basicInfo.seeker_genders;
    } else {
      answers['seeker_gender'] = basicInfo.seeker_gender;
    }
    if (profile?.name) {
      answers['seeker_full_name'] = profile.name;
    }
    if (profile?.phone) {
      answers['seeker_phone'] = profile.phone;
    }

    if (experience) {
      if (Array.isArray(experience.experience_types)) {
        answers['experience_types'] = [...experience.experience_types];
      }
      if (experience.experience_types_other) {
        answers['experience_types_other'] = experience.experience_types_other;
      }
      if (Array.isArray(experience.medicine_options_comfort)) {
        answers['medicine_options_comfort'] = [...experience.medicine_options_comfort];
      }
      if (Array.isArray(experience.support_types)) {
        answers['support_types'] = [...experience.support_types];
      }
      if (Array.isArray(experience.travel_willingness)) {
        answers['travel_willingness'] = [...experience.travel_willingness];
      }
      if (experience.travel_distance) {
        answers['travel_distance'] = experience.travel_distance;
      }
      if (experience.focus_preferences && typeof experience.focus_preferences === 'object') {
        const focusPrefs = experience.focus_preferences as Record<string, unknown>;
        Object.entries(focusPrefs).forEach(([groupId, value]) => {
          if (Array.isArray(value)) {
            answers[groupId] = value as string[];
          }
        });
      }
      if (experience.focus_special_other_text) {
        answers['focus_special_other_text'] = experience.focus_special_other_text;
      }
      if (Array.isArray(experience.prefer_specialist_in_groups)) {
        answers['prefer_specialist_in_groups'] = [...experience.prefer_specialist_in_groups];
      }
      if (experience.religion_alignment_important) {
        answers['religion_alignment_important'] = experience.religion_alignment_important;
      }
      if (Array.isArray(experience.seeker_beliefs)) {
        answers['seeker_beliefs'] = [...experience.seeker_beliefs];
      }
      if (experience.seeker_beliefs_other_text) {
        answers['seeker_beliefs_other_text'] = experience.seeker_beliefs_other_text;
      }
    }

    if (medical) {
      answers['currently_on_meds'] = medical.currently_on_meds;
      if (Array.isArray(medical.clinical_diagnoses)) {
        answers['clinical_diagnoses'] = [...medical.clinical_diagnoses];
      }
      answers['budget_band'] = medical.budget_band;
      if (medical.additional_info) {
        answers['additional_info'] = medical.additional_info;
      }
    }

    return {
      answers,
      seekerId,
      sessionId,
      imageUrl: seekerRow?.image_url ?? null
    };
  }

  async updateProfile(updates: { name?: string; phone?: string | null }): Promise<SeekerProfile> {
    const { data: { user }, error: authError } = await this.supabase.auth.getUser();

    if (authError || !user) {
      throw new Error('Not authenticated');
    }

    const payload: { full_name?: string | null; phone?: string | null } = {};

    if (updates.name !== undefined) {
      const trimmed = updates.name?.trim() ?? '';
      payload.full_name = trimmed.length > 0 ? trimmed : null;
    }

    if (updates.phone !== undefined) {
      const trimmed = updates.phone?.trim();
      payload.phone = trimmed && trimmed.length > 0 ? trimmed : null;
    }

    const { data, error } = await this.supabase
      .from('profiles')
      .update(payload)
      .eq('id', user.id)
      .select('id, full_name, email, phone, created_at')
      .single();

    if (error) {
      throw new Error(`Failed to update profile: ${error.message}`);
    }

    let seekerImageUrl: string | null = null;
    const { data: seekerRow, error: seekerError } = await this.supabase
      .from('seekers')
      .select('image_url')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!seekerError && seekerRow?.image_url) {
      seekerImageUrl = seekerRow.image_url;
    }

    return {
      id: data.id,
      name: data.full_name ?? undefined,
      email: data.email ?? undefined,
      phone: data.phone ?? null,
      created_at: data.created_at,
      imageUrl: seekerImageUrl
    };
  }

  async getSeekerIdForCurrentUser(): Promise<string | null> {
    const { data: { user }, error: authError } = await this.supabase.auth.getUser();

    if (authError || !user) {
      throw new Error('Not authenticated');
    }

    // Use the database function to get seeker ID
    const { data, error } = await this.supabase.rpc('get_seeker_id_for_user', {
      auth_user_id: user.id
    });

    if (error) {
      console.error('Failed to get seeker ID:', error);
      return null;
    }

    return data;
  }

  async getSeekerTimezone(): Promise<string | null> {
    try {
      const seekerId = await this.getSeekerIdForCurrentUser();
      if (!seekerId) {
        return null;
      }

      const { data, error } = await this.supabase
        .from('seeker_basic_info')
        .select('timezone')
        .eq('seeker_id', seekerId)
        .maybeSingle();

      if (error) {
        console.error('Failed to get seeker timezone:', error);
        return null;
      }

      return data?.timezone || null;
    } catch (error) {
      console.error('Error fetching seeker timezone:', error);
      return null;
    }
  }

  async getSeekerPostcode(): Promise<string | null> {
    try {
      const seekerId = await this.getSeekerIdForCurrentUser();
      if (!seekerId) {
        return null;
      }

      const { data, error } = await this.supabase
        .from('seeker_basic_info')
        .select('location_postcode')
        .eq('seeker_id', seekerId)
        .maybeSingle();

      if (error) {
        console.error('Failed to get seeker postcode:', error);
        return null;
      }

      return data?.location_postcode || null;
    } catch (error) {
      console.error('Error fetching seeker postcode:', error);
      return null;
    }
  }

  async getMatchedGuide(seekerId: string): Promise<Match | null> {
    // First, find the active match from the matches table directly.
    // This avoids returning an old invalidated match when a newer active match exists.
    const { data: activeMatchRow, error: activeMatchError } = await this.supabase
      .from('matches')
      .select('id')
      .eq('seeker_id', seekerId)
      .eq('status', 'active')
      .order('rank', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (activeMatchError) {
      console.warn('[getMatchedGuide] Error querying active match:', activeMatchError);
    }

    // Query the view, filtered to the active match if we found one
    let query = this.supabase
      .from('seeker_guide_view')
      .select('*')
      .eq('seeker_id', seekerId);

    if (activeMatchRow) {
      query = query.eq('match_id', activeMatchRow.id);
    } else {
      // No active match — fall back to most recent match of any status
      query = query.order('match_created_at', { ascending: false });
    }

    const { data: matchData, error: matchError } = await query
      .limit(1)
      .maybeSingle();

    if (matchError) {
      console.warn('[getMatchedGuide] Failed to fetch match from seeker_guide_view:', matchError);
    }

    // If the view returned nothing, try a direct matches query as fallback.
    // This handles cases where RLS or view security blocks the view query.
    if (!matchData) {
      console.warn('[getMatchedGuide] No match from view. Trying direct matches query for seeker:', seekerId);
      const { data: directMatch, error: directError } = await this.supabase
        .from('matches')
        .select('id, seeker_id, guide_id, match_score, score_breakdown, reasoning, created_at, rank')
        .eq('seeker_id', seekerId)
        .eq('status', 'active')
        .order('rank', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (directError) {
        console.warn('[getMatchedGuide] Direct matches query also failed:', directError);
        return null;
      }

      if (!directMatch) {
        console.warn('[getMatchedGuide] No match found in matches table for seeker:', seekerId);
        return null;
      }

      // We have a match row but couldn't get guide details from the view.
      // Fetch guide details directly.
      console.log('[getMatchedGuide] Found direct match, fetching guide details for guide:', directMatch.guide_id);

      const [{ data: profile }, { data: enhancedProfile }, { data: experience }, { data: professional }] = await Promise.all([
        this.supabase.from('profiles').select('id, full_name, email, "backgroundChecked"').eq('id', directMatch.guide_id).maybeSingle(),
        this.supabase.from('guides_enhanced_profile').select('display_name, about, image_url').eq('guide_id', directMatch.guide_id).maybeSingle(),
        this.supabase.from('guide_professional_experience').select('experience_types, support_types, focus_areas, special_groups, religious_beliefs').eq('guide_id', directMatch.guide_id).maybeSingle(),
        this.supabase.from('guide_professional').select('years_practicing').eq('guide_id', directMatch.guide_id).maybeSingle(),
      ]);

      const guideName = enhancedProfile?.display_name || profile?.full_name || 'Guide';

      const guide: Guide = {
        id: directMatch.guide_id,
        name: guideName,
        title: 'Licensed Therapist',
        experience: professional?.years_practicing ? `${professional.years_practicing} practicing` : '5+ years experience',
        rating: 4.8,
        specializations: experience?.focus_areas?.slice(0, 3) || ['Therapy', 'Guidance'],
        description: enhancedProfile?.about || 'Experienced guide specializing in therapeutic approaches.',
        image: enhancedProfile?.image_url ?? null,
        location: 'Location not specified',
        experience_types: experience?.experience_types || [],
        support_types: experience?.support_types || [],
        focus_areas: experience?.focus_areas || [],
        special_groups: experience?.special_groups || [],
        religious_beliefs: experience?.religious_beliefs || [],
        backgroundChecked: typeof profile?.backgroundChecked === 'boolean' ? profile.backgroundChecked : null
      };

      let matchStatus: MatchStatus | null = null;
      let matchEndedAt: string | null = null;
      try {
        const { data: statusRow } = await this.supabase
          .from('matches')
          .select('status, ended_at')
          .eq('id', directMatch.id)
          .maybeSingle();
        if (statusRow) {
          matchStatus = statusRow.status as MatchStatus;
          matchEndedAt = statusRow.ended_at ?? null;
        }
      } catch (_e) {
        // Status lookup is best-effort
      }

      return {
        id: directMatch.id,
        seeker_id: directMatch.seeker_id,
        guide_id: directMatch.guide_id,
        match_score: directMatch.match_score,
        score_breakdown: directMatch.score_breakdown,
        reasoning: directMatch.reasoning,
        created_at: directMatch.created_at,
        updated_at: directMatch.created_at,
        status: matchStatus,
        ended_at: matchEndedAt,
        rank: directMatch.rank,
        guide_details: guide,
      };
    }

    const guide: Guide = {
      id: matchData.guide_id,
      name: matchData.guide_name || 'Guide',
      title: 'Licensed Therapist', // TODO: Add title field to database
      experience: matchData.guide_years_practicing ? 
        `${matchData.guide_years_practicing} practicing` : 
        '5+ years experience',
      rating: 4.8, // TODO: Add ratings system
      specializations: matchData.guide_focus_areas?.slice(0, 3) || ['Therapy', 'Guidance'],
      description: matchData.guide_about || 'Experienced guide specializing in therapeutic approaches.',
      image: matchData.guide_image_url,
      location: 'Location not specified', // TODO: Add location field
      experience_types: matchData.guide_experience_types || [],
      support_types: matchData.guide_support_types || [],
      focus_areas: matchData.guide_focus_areas || [],
      special_groups: matchData.guide_special_groups || [],
      religious_beliefs: matchData.guide_religious_beliefs || [],
      backgroundChecked: matchData.guide_background_checked
    };

    let matchStatus: MatchStatus | null = null;
    let matchEndedAt: string | null = null;
    try {
      const { data: statusRow, error: statusError } = await this.supabase
        .from('matches')
        .select('status, ended_at')
        .eq('id', matchData.match_id)
        .maybeSingle();
      if (statusError) {
        console.warn('Unable to load match status for seeker dashboard', statusError);
      } else if (statusRow) {
        matchStatus = statusRow.status as MatchStatus;
        matchEndedAt = statusRow.ended_at ?? null;
      }
    } catch (statusErr) {
      console.warn('Unexpected error loading match status for seeker dashboard', statusErr);
    }

    return {
      id: matchData.match_id,
      seeker_id: matchData.seeker_id,
      guide_id: matchData.guide_id,
      match_score: matchData.match_score,
      score_breakdown: matchData.score_breakdown,
      reasoning: matchData.reasoning,
      created_at: matchData.match_created_at,
      updated_at: matchData.match_created_at, // Using same as created for now
      status: matchStatus,
      ended_at: matchEndedAt,
      rank: matchData.rank,
      guide_details: guide,
    };
  }

  async getUpcomingSession(primaryId: string, additionalIds: string[] = []): Promise<Session | null> {
    console.log('[getUpcomingSession] Called with primaryId:', primaryId, 'additionalIds:', additionalIds);

    // Query using the authenticated user's ID directly
    // The seeker_id column in scheduled_sessions references auth.users(id)
    // Note: scheduled_by column may not exist until migration is run - query will still work
    const { data: sessionData, error: sessionError } = await this.supabase
      .from('scheduled_sessions')
      .select('*')
      .eq('seeker_id', primaryId)
      .in('session_status', ['scheduled', 'confirmed'])
      .order('session_date', { ascending: true })
      .order('session_time', { ascending: true })
      .limit(1)
      .maybeSingle();

    console.log('[getUpcomingSession] Query result:', {
      sessionData,
      sessionError,
      hasData: !!sessionData,
      sessionType: sessionData?.session_type,
      sessionStatus: sessionData?.session_status,
      sessionDate: sessionData?.session_date,
      sessionTime: sessionData?.session_time
    });

    if (sessionError) {
      console.warn('[getUpcomingSession] Failed to fetch upcoming session:', sessionError);
      return null;
    }

    if (!sessionData) {
      console.log('[getUpcomingSession] No session data found for primaryId:', primaryId);
      return null;
    }

    // Get guide name - use display_name if set, otherwise fall back to full_name
    const [{ data: guideProfile, error: guideError }, { data: enhancedProfile }] = await Promise.all([
      this.supabase
        .from('profiles')
        .select('full_name')
        .eq('id', sessionData.guide_id)
        .single(),
      this.supabase
        .from('guides_enhanced_profile')
        .select('display_name')
        .eq('guide_id', sessionData.guide_id)
        .maybeSingle()
    ]);

    // Use display_name if available, otherwise fall back to full_name
    const guideName = enhancedProfile?.display_name || guideProfile?.full_name || 'Guide';

    console.log('[getUpcomingSession] Guide profile lookup:', {
      guideId: sessionData.guide_id,
      displayName: enhancedProfile?.display_name,
      fullName: guideProfile?.full_name,
      guideName,
      guideError
    });

    const session = {
      ...sessionData,
      guide_name: guideName
    };

    console.log('[getUpcomingSession] Returning session:', session);
    return session;
  }

  async hasCompletedConsultationSession(primaryId: string, additionalIds: string[] = []): Promise<boolean> {
    if (!primaryId) {
      return false;
    }

    const { data, error } = await this.supabase
      .from('scheduled_sessions')
      .select('id')
      .eq('seeker_id', primaryId)
      .eq('session_type', 'consultation')
      .eq('session_status', 'completed')
      .order('session_date', { ascending: false })
      .order('session_time', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.warn('Failed to check completed consultation session:', error);
      return false;
    }

    return !!data;
  }

  async getResources(limit: number = 3, uploadedBy?: string | null): Promise<Resource[]> {
    const query = this.supabase
      .from('resources')
      .select(
        `id, name, author, category, description, tags, file_name, file_path, file_type, mime_type, thumbnail_url, content_url, downloads, is_active, uploaded_at, uploaded_by`
      )
      .eq('is_active', true)
      .order('uploaded_at', { ascending: false });

    if (uploadedBy) {
      query.eq('uploaded_by', uploadedBy);
    }

    if (limit) {
      query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Failed to load resources:', error);
      return [];
    }

    return (data ?? []).map((resource) => {
      const publicUrl = this.supabase.storage
        .from('resources')
        .getPublicUrl(resource.file_path).data.publicUrl;

      return {
        ...resource,
        content_url: resource.content_url ?? publicUrl
      } as Resource;
    });
  }

  private normalizeOfferStatus(status: string | null | undefined): PackageOfferStatus {
    const normalized = (status ?? 'draft').toLowerCase();
    const allowedStatuses: PackageOfferStatus[] = ['draft', 'sent', 'accepted', 'rejected', 'withdrawn'];
    return allowedStatuses.includes(normalized as PackageOfferStatus)
      ? (normalized as PackageOfferStatus)
      : 'draft';
  }

  private normalizeOfferUnitStatus(status: string | null | undefined): PackageOfferUnitStatus {
    const normalized = (status ?? 'unallocated').toLowerCase();
    const allowedStatuses: PackageOfferUnitStatus[] = ['unallocated', 'scheduled', 'fulfilled', 'cancelled'];
    return allowedStatuses.includes(normalized as PackageOfferUnitStatus)
      ? (normalized as PackageOfferUnitStatus)
      : 'unallocated';
  }

  private mapPackageOffer(row: PackageOfferRow | null): PackageOffer | null {
    if (!row) {
      return null;
    }

    const units: PackageOfferUnit[] = (row.package_offer_units ?? []).map((unit) => {
      const scheduled = unit.scheduled_session ?? null;

      return {
        id: unit.id,
        templateComponentId: unit.template_component_id ?? null,
        componentName: unit.component_name ?? 'Session',
        componentType: unit.component_type ?? DEFAULT_COMPONENT_TYPE,
        unitIndex: unit.unit_index ?? 0,
        status: this.normalizeOfferUnitStatus(unit.status),
        unitPriceCents: unit.unit_price_cents ?? 0,
        notes: unit.notes ?? null,
        scheduledSessionId: unit.scheduled_session_id ?? null,
        sessionDate: scheduled?.session_date ?? null,
        sessionTime: scheduled?.session_time ?? null,
        durationMinutes: scheduled?.duration_minutes ?? null,
        sessionStatus: scheduled?.session_status ?? null
      };
    });

    const sortedUnits = [...units].sort((a, b) => {
      const typeCompare = a.componentType.localeCompare(b.componentType);
      if (typeCompare !== 0) return typeCompare;
      const nameCompare = a.componentName.localeCompare(b.componentName);
      if (nameCompare !== 0) return nameCompare;
      return a.unitIndex - b.unitIndex;
    });

    return {
      id: row.id,
      guideId: row.guide_id ?? '',
      seekerId: row.seeker_id ?? '',
      name: row.name ?? 'Package Offer',
      description: row.description,
      durationMonths: row.duration_months ?? undefined,
      status: this.normalizeOfferStatus(row.status),
      subtotalCents: row.subtotal_cents ?? 0,
      totalCents: row.total_cents ?? row.subtotal_cents ?? 0,
      createdAt: row.created_at ?? new Date().toISOString(),
      units: sortedUnits
    };
  }

  async getLatestPackageOfferForUser(authUserId: string): Promise<PackageOffer | null> {
    if (!authUserId) {
      return null;
    }

    const { data, error } = await this.supabase
      .from('package_offers')
      .select(
        `
          id,
          guide_id,
          seeker_id,
          name,
          description,
          duration_months,
          status,
          subtotal_cents,
          total_cents,
          created_at,
          package_offer_units (
            id,
            template_component_id,
            component_name,
            component_type,
            unit_index,
            status,
            unit_price_cents,
            notes,
            scheduled_session_id,
            scheduled_session:scheduled_sessions!package_offer_units_scheduled_session_id_fkey (
              id,
              session_date,
              session_time,
              duration_minutes,
              session_status
            )
          )
        `
      )
      .eq('seeker_id', authUserId)
      .in('status', VISIBLE_PACKAGE_STATUSES)
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      console.warn('Failed to load package offer:', error);
      return null;
    }

    const offerRow = Array.isArray(data) ? (data[0] as PackageOfferRow | undefined) : undefined;
    return this.mapPackageOffer(offerRow ?? null);
  }

  async getPackageOffersForUser(authUserId: string): Promise<{
    activeEngagements: PackageOffer[];
    activeEngagement: PackageOffer | null;
    pendingOffer: PackageOffer | null;
  }> {
    if (!authUserId) {
      return { activeEngagements: [], activeEngagement: null, pendingOffer: null };
    }

    const { data, error } = await this.supabase
      .from('package_offers')
      .select(
        `
          id,
          guide_id,
          seeker_id,
          name,
          description,
          duration_months,
          status,
          subtotal_cents,
          total_cents,
          created_at,
          package_offer_units (
            id,
            template_component_id,
            component_name,
            component_type,
            unit_index,
            status,
            unit_price_cents,
            notes,
            scheduled_session_id,
            scheduled_session:scheduled_sessions!package_offer_units_scheduled_session_id_fkey (
              id,
              session_date,
              session_time,
              duration_minutes,
              session_status
            )
          )
        `
      )
      .eq('seeker_id', authUserId)
      .in('status', VISIBLE_PACKAGE_STATUSES)
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('Failed to load package offers:', error);
      return { activeEngagements: [], activeEngagement: null, pendingOffer: null };
    }

    const offers = (data || []) as PackageOfferRow[];

    const acceptedOffers = offers
      .filter((offer) => offer.status?.toLowerCase() === 'accepted')
      .map((offer) => this.mapPackageOffer(offer))
      .filter((offer): offer is PackageOffer => !!offer);

    // Find the most recent pending offer (draft or sent, but not accepted)
    const pendingOffer = offers.find(offer =>
      ['draft', 'sent'].includes(offer.status?.toLowerCase() ?? '')
    );

    return {
      activeEngagements: acceptedOffers,
      activeEngagement: acceptedOffers[0] ?? null,
      pendingOffer: this.mapPackageOffer(pendingOffer ?? null)
    };
  }

  async getHomePageData(): Promise<HomePageData> {
    console.log('[getHomePageData] Starting to fetch home page data...');
    try {
      const { user, profile } = await this.getCurrentUser();
      console.log('[getHomePageData] Current user:', { userId: user?.id, profileName: profile?.name });

      if (!user) {
        console.log('[getHomePageData] No user found, returning empty data');
        return {
          profile: null,
          match: null,
          upcomingSession: null,
          resources: [],
          packageOffer: null,
          activeEngagement: null,
          activeEngagements: [],
          hasCompletedConsultation: false
        };
      }

      // Get the seeker ID for this auth user
      const seekerId = await this.getSeekerIdForCurrentUser();
      console.log('[getHomePageData] Seeker ID:', seekerId, 'Auth User ID:', user.id);

      if (!seekerId) {
        console.log('[getHomePageData] No seeker ID found (user not onboarded)');
        // User is authenticated but hasn't completed onboarding yet
        const { activeEngagement, activeEngagements, pendingOffer } = await this.getPackageOffersForUser(user.id);
        return {
          profile,
          match: null,
          upcomingSession: null,
          resources: [],
          packageOffer: pendingOffer,
          activeEngagement,
          activeEngagements,
          hasCompletedConsultation: false
        };
      }

      console.log('[getHomePageData] Fetching match, packages, resources, session, and consultation status...');
      const match = await this.getMatchedGuide(seekerId);
      const [packageOffers, resources, upcomingSession, hasCompletedConsultation] = await Promise.all([
        this.getPackageOffersForUser(user.id),
        match?.guide_id ? this.getResources(3, match.guide_id) : Promise.resolve<Resource[]>([]),
        this.getUpcomingSession(user.id, [seekerId]),
        this.hasCompletedConsultationSession(user.id, [seekerId])
      ]);

      console.log('[getHomePageData] Fetched data:', {
        hasMatch: !!match,
        matchId: match?.id,
        guideId: match?.guide_id,
        hasUpcomingSession: !!upcomingSession,
        upcomingSessionId: upcomingSession?.id,
        upcomingSessionType: upcomingSession?.session_type,
        upcomingSessionStatus: upcomingSession?.session_status,
        upcomingSessionDate: upcomingSession?.session_date,
        hasPackageOffer: !!packageOffers.pendingOffer,
        hasActiveEngagement: !!packageOffers.activeEngagement,
        activeEngagementsCount: packageOffers.activeEngagements.length,
        hasCompletedConsultation,
        resourcesCount: resources.length
      });

      const homePageData = {
        profile,
        match,
        upcomingSession,
        resources,
        packageOffer: packageOffers.pendingOffer,
        activeEngagement: packageOffers.activeEngagement,
        activeEngagements: packageOffers.activeEngagements,
        hasCompletedConsultation
      };

      console.log('[getHomePageData] Returning home page data with upcomingSession:', homePageData.upcomingSession);
      return homePageData;
    } catch (error) {
      console.error('[getHomePageData] Failed to fetch home page data:', error);
      return {
        profile: null,
        match: null,
        upcomingSession: null,
        resources: [],
        packageOffer: null,
        activeEngagement: null,
        activeEngagements: [],
        hasCompletedConsultation: false
      };
    }
  }

  // Session management functions for seeker
  async rescheduleSession(sessionId: string, newDate: string, newTime: string): Promise<void> {
    // Use the new session-reschedule Edge Function
    const { data, error } = await this.supabase.functions.invoke('session-reschedule', {
      body: {
        session_id: sessionId,
        new_date: newDate,
        new_time: newTime,
      },
    });

    if (error) {
      throw new Error(error.message || 'Failed to reschedule session');
    }

    console.log('Session rescheduled via Edge Function:', data);
  }

  async confirmSession(sessionId: string): Promise<void> {
    // Call the confirm-session edge function
    const { data: { session: authSession }, error: authError } = await this.supabase.auth.getSession();

    if (authError || !authSession?.access_token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `${(window as any).__ENV__?.SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL}/functions/v1/confirm-session`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authSession.access_token}`,
        },
        body: JSON.stringify({ session_id: sessionId }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to confirm session');
    }

    const data = await response.json();
    console.log('Session confirmed via Edge Function:', data);
  }

  async updateOnboardingAndProfile(answers: OnboardingAnswers): Promise<void> {
    const { data: { user }, error: authError } = await this.supabase.auth.getUser();

    if (authError || !user) {
      throw new Error('Not authenticated');
    }

    const seekerId = await this.getSeekerIdForCurrentUser();
    if (!seekerId) {
      throw new Error('No seeker profile found');
    }

    // Get existing session ID from basic info
    const { data: basicInfo } = await this.supabase
      .from('seeker_basic_info')
      .select('session_id')
      .eq('seeker_id', seekerId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    const sessionId = basicInfo?.session_id;
    if (!sessionId) {
      throw new Error('No onboarding session found');
    }

    // Update profiles table
    const profilePayload: { full_name?: string; phone?: string } = {};
    if (answers.seeker_full_name !== undefined) {
      profilePayload.full_name = answers.seeker_full_name;
    }
    if (answers.seeker_phone !== undefined) {
      profilePayload.phone = answers.seeker_phone;
    }

    if (Object.keys(profilePayload).length > 0) {
      const { error: profileError } = await this.supabase
        .from('profiles')
        .update(profilePayload)
        .eq('id', user.id);

      if (profileError) {
        throw new Error(`Failed to update profile: ${profileError.message}`);
      }
    }

    // Update seeker_basic_info
    const basicInfoPayload: Record<string, any> = {};
    if (answers.seeker_email !== undefined) basicInfoPayload.seeker_email = answers.seeker_email;
    if (answers.seeker_over_18 !== undefined) basicInfoPayload.seeker_over_18 = answers.seeker_over_18;
    if (answers.location_postcode !== undefined) {
      basicInfoPayload.location_postcode = answers.location_postcode;
      // Look up city, state, and timezone from the zip code
      const zipDigits = (answers.location_postcode || '').replace(/\D/g, '');
      if (zipDigits.length === 5) {
        const { data: zipRows } = await this.supabase
          .from('postal_code_timezones')
          .select('city, state, timezone_id')
          .eq('country', 'US')
          .eq('postal_code', zipDigits)
          .limit(1);
        if (zipRows && zipRows.length > 0) {
          basicInfoPayload.city = zipRows[0].city;
          basicInfoPayload.state = zipRows[0].state;
          basicInfoPayload.timezone = zipRows[0].timezone_id;
        }
      }
    }
    // Handle seeker_gender as either array (new multi-select) or string (legacy)
    if (answers.seeker_gender !== undefined) {
      if (Array.isArray(answers.seeker_gender)) {
        basicInfoPayload.seeker_genders = answers.seeker_gender;
        basicInfoPayload.seeker_gender = answers.seeker_gender[0] || '';
      } else {
        basicInfoPayload.seeker_gender = answers.seeker_gender;
        basicInfoPayload.seeker_genders = [answers.seeker_gender];
      }
    }

    if (Object.keys(basicInfoPayload).length > 0) {
      basicInfoPayload.updated_at = new Date().toISOString();
      const { error: basicError } = await this.supabase
        .from('seeker_basic_info')
        .update(basicInfoPayload)
        .eq('seeker_id', seekerId)
        .eq('session_id', sessionId);

      if (basicError) {
        throw new Error(`Failed to update basic info: ${basicError.message}`);
      }
    }

    // Update seeker_experience_preferences
    const experiencePayload: Record<string, any> = {};
    if (answers.experience_types !== undefined) experiencePayload.experience_types = answers.experience_types;
    if (answers.experience_types_other !== undefined) experiencePayload.experience_types_other = answers.experience_types_other;
    if (answers.medicine_options_comfort !== undefined) experiencePayload.medicine_options_comfort = answers.medicine_options_comfort;
    if (answers.support_types !== undefined) experiencePayload.support_types = answers.support_types;
    if (answers.travel_willingness !== undefined) experiencePayload.travel_willingness = answers.travel_willingness;
    if (answers.travel_distance !== undefined) experiencePayload.travel_distance = answers.travel_distance;

    // Handle focus_preferences as JSONB
    const focusPreferences: Record<string, string[]> = {};
    const focusGroups = [
      'focus_mental_health_pref',
      'focus_trauma_pref',
      'focus_addiction_pref',
      'focus_relationships_pref',
      'focus_transitions_pref',
      'focus_growth_pref',
      'focus_special_pref'
    ];

    focusGroups.forEach(groupId => {
      if (Array.isArray(answers[groupId])) {
        focusPreferences[groupId] = answers[groupId] as string[];
      }
    });

    if (Object.keys(focusPreferences).length > 0) {
      experiencePayload.focus_preferences = focusPreferences;
    }

    if (answers.focus_special_other_text !== undefined) experiencePayload.focus_special_other_text = answers.focus_special_other_text;
    if (answers.prefer_specialist_in_groups !== undefined) experiencePayload.prefer_specialist_in_groups = answers.prefer_specialist_in_groups;
    if (answers.religion_alignment_important !== undefined) experiencePayload.religion_alignment_important = answers.religion_alignment_important;
    if (answers.seeker_beliefs !== undefined) experiencePayload.seeker_beliefs = answers.seeker_beliefs;
    if (answers.seeker_beliefs_other_text !== undefined) experiencePayload.seeker_beliefs_other_text = answers.seeker_beliefs_other_text;

    if (Object.keys(experiencePayload).length > 0) {
      experiencePayload.updated_at = new Date().toISOString();
      const { error: experienceError } = await this.supabase
        .from('seeker_experience_preferences')
        .update(experiencePayload)
        .eq('seeker_id', seekerId)
        .eq('session_id', sessionId);

      if (experienceError) {
        throw new Error(`Failed to update experience preferences: ${experienceError.message}`);
      }
    }

    // Update seeker_medical_and_final
    const medicalPayload: Record<string, any> = {};
    if (answers.currently_on_meds !== undefined) medicalPayload.currently_on_meds = answers.currently_on_meds;
    if (answers.clinical_diagnoses !== undefined) medicalPayload.clinical_diagnoses = answers.clinical_diagnoses;
    if (answers.budget_band !== undefined) medicalPayload.budget_band = answers.budget_band;
    if (answers.additional_info !== undefined) medicalPayload.additional_info = answers.additional_info;

    if (Object.keys(medicalPayload).length > 0) {
      medicalPayload.updated_at = new Date().toISOString();
      const { error: medicalError } = await this.supabase
        .from('seeker_medical_and_final')
        .update(medicalPayload)
        .eq('seeker_id', seekerId)
        .eq('session_id', sessionId);

      if (medicalError) {
        throw new Error(`Failed to update medical and final info: ${medicalError.message}`);
      }
    }
  }
}

// Create a function to get the service instance instead of creating it immediately
let _seekerDataService: SeekerDataService | null = null;

export function getSeekerDataService(): SeekerDataService {
  if (!_seekerDataService) {
    _seekerDataService = new SeekerDataService();
  }
  return _seekerDataService;
}

// For backward compatibility
export const seekerDataService = {
  getHomePageData: () => getSeekerDataService().getHomePageData(),
  getCurrentUser: () => getSeekerDataService().getCurrentUser(),
  getSeekerTimezone: () => getSeekerDataService().getSeekerTimezone(),
  getSeekerPostcode: () => getSeekerDataService().getSeekerPostcode(),
  getMatchedGuide: (seekerId: string) => getSeekerDataService().getMatchedGuide(seekerId),
  getUpcomingSession: (primaryId: string, additionalIds: string[] = []) =>
    getSeekerDataService().getUpcomingSession(primaryId, additionalIds),
  getResources: (limit?: number) => getSeekerDataService().getResources(limit),
  getOnboardingEditData: () => getSeekerDataService().getOnboardingEditData(),
  hasCompletedConsultationSession: (primaryId: string, additionalIds: string[] = []) =>
    getSeekerDataService().hasCompletedConsultationSession(primaryId, additionalIds),
  getLatestPackageOfferForUser: (authUserId: string) =>
    getSeekerDataService().getLatestPackageOfferForUser(authUserId),
  updateProfile: (updates: { name?: string; phone?: string | null }) =>
    getSeekerDataService().updateProfile(updates),
  rescheduleSession: (sessionId: string, newDate: string, newTime: string) =>
    getSeekerDataService().rescheduleSession(sessionId, newDate, newTime),
  confirmSession: (sessionId: string) =>
    getSeekerDataService().confirmSession(sessionId),
  updateOnboardingAndProfile: (answers: OnboardingAnswers) =>
    getSeekerDataService().updateOnboardingAndProfile(answers)
};
